
import { constants, promises as fs } from 'fs';
import bent from 'bent';
import path from 'path';
import { Parser } from 'm3u8-parser';

const GET = bent('GET');
const HEAD = bent('HEAD');
const DOWNLOAD = bent('buffer');

const m3u8Manager = class {

  constructor(url) {
    this.url = url;
    this.totalSize = 0;
    this.extension = path.extname(url);
    this.manifest = {};
  }

  // Methods
  async parse() {
    if ( this.extension === '.m3u8' ) {
      this.manifest = await this._requestManifest(this.url);
      this.manifest.totalBytes = this.totalSize;
      if (!this.manifest.playlistType) delete this.manifest.playlistBytes;
    } else {
      throw new Error(`File type ${this.extension} not supported`);
    }

    return this.manifest;
  }

  async _requestManifest(url) {
    const response = await GET(url);
    const body = await response.text();
    const manifest = await this._parseManifest(url, body);
    this.totalSize += manifest.playlistBytes;
    return manifest;
  }

  async _parseManifest(url, data) {
    const baseURL = path.dirname(url);
    const parser = new Parser();
    parser.push( data );
    parser.end();

    const parsedManifest = parser.manifest;
    const { segments = [], playlists = [] } = parsedManifest;  

    // Variant Playlists
    // for-loops are faster and scoped better for this iteration
    const plist = [];
    for ( let i = 0; i < playlists.length; i++ ) {
      const playlistURL = `${baseURL}/${playlists[i].uri}`; 
      plist.push( this._requestManifest(playlistURL) );
    }  
    const variants = await Promise.all( plist );
    for ( let i = 0; i < playlists.length; i++ ) {
      parsedManifest.playlists[i].variant = variants[i]; 
    }

    // Variant Segments
    const segmentList = segments.map( segment => HEAD(`${baseURL}/${segment.uri}`) );
    const getHeaders = await Promise.all( segmentList );
    const headers = getHeaders.reduce( (h, obj) => h.concat(obj.headers), [] );
    parsedManifest.playlistBytes = headers.reduce( (size, header) => size += parseInt(header['content-length']), 0 );
    return parsedManifest;
  }

  async downloadPlaylist() {
    // Download Main Playlist Manifest
    const baseURL = path.dirname(this.url);
    const filename = path.basename(this.url); 
    const baseDir = path.basename(filename, '.m3u8'); 
    const filepath = `${baseDir}/${filename}`;
    await fs.mkdir(baseDir, { recursive: true });
    await this._downloadFile(this.url, filepath);

    // Get All Variant Playlists
    const manifest = await this._requestManifest(this.url);
    const playlists = manifest.playlists.reduce( (p, obj) => p.concat(obj.uri), [] );
    
    for ( let i = 0; i < playlists.length; i++ ) {
      const downloads = [];
      // Download Each Variant Playlist
      const p = playlists[i];
      const playlistURL = `${baseURL}/${p}`;
      const dirname = `${baseDir}/${path.dirname(p)}`;
      const filepath = `${baseDir}/${p}`; 
      await fs.mkdir(dirname, { recursive: true });
      await this._downloadFile( playlistURL, filepath );

      // Get All Segments From Each Variant Playlist
      const segments = await this._requestManifest( playlistURL );
      for ( let j = 0; j < segments.segments.length; j++ ) {
        const segment = segments.segments[j];
        const segmentDir = path.dirname(filepath);
        const segmentURL = `${baseURL}/${path.dirname(p)}/${segment.uri}`;
        const segmentFilepath = `${segmentDir}/${segment.uri}`;
        downloads.push( this._downloadFile( segmentURL, segmentFilepath ) );

        // Download any additional files mapped in segments
        if (segment.map.uri) {
          const mapURL = `${baseURL}/${path.dirname(p)}/${segment.map.uri}`;
          const mapFilepath = `${segmentDir}/${segment.map.uri}`;
          await this._downloadFile( mapURL, mapFilepath );
        }
      }
      await Promise.all( downloads );
    } 
  }

  async _downloadFile(url, filename) {
    try {
      await fs.access(filename, constants.F_OK);
    } catch (err) {
      console.log( `${url} => ${filename}` );
      const response = await DOWNLOAD(url); 
      await fs.writeFile(filename, response);
    }
  }
};

export default m3u8Manager;
